import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { SHOW_DEVELOPMENT_PREVIEWS } from '../../config/developmentPreviews';
import {
  Activity,
  AlertTriangle,
  ArrowUpDown,
  CheckCircle2,
  Clock3,
  LineChart,
  Link2,
  PlusCircle,
  Scan,
  Stethoscope,
} from 'lucide-react';
import WalkInUrgentCarePage from './WalkInUrgentCarePage';

type Severity = 'critical' | 'high' | 'routine';
type Status = 'queued' | 'processing' | 'resulted' | 'flagged';

type LabResult = {
  id: string;
  patient: string;
  mrn: string;
  location: string;
  reason: string;
  test: string;
  status: Status;
  severity: Severity;
  value: string;
  units: string;
  reference: string;
  delta: number;
  abnormal: boolean;
  confidence: number;
  etaMinutes: number;
  updatedAgo: string;
};

const severityMeta: Record<Severity, { label: string; className: string }> = {
  critical: { label: 'Critical', className: 'bg-rose-50 text-rose-600 border border-rose-100' },
  high: { label: 'High', className: 'bg-amber-50 text-amber-600 border border-amber-100' },
  routine: { label: 'Routine', className: 'bg-emerald-50 text-emerald-600 border border-emerald-100' },
};

const statusMeta: Record<Status, { label: string; className: string }> = {
  queued: { label: 'Queued', className: 'bg-slate-50 text-slate-600 border border-slate-200' },
  processing: { label: 'Processing', className: 'bg-sky-50 text-sky-600 border border-sky-100' },
  resulted: { label: 'Resulted', className: 'bg-emerald-50 text-emerald-600 border border-emerald-100' },
  flagged: { label: 'Escalate', className: 'bg-rose-50 text-rose-600 border border-rose-100' },
};

const statusOptions: { value: 'all' | Status; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'resulted', label: 'Resulted' },
  { value: 'flagged', label: 'Flagged' },
];

const severityOptions: { value: 'all' | Severity; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'routine', label: 'Routine' },
];

type SortKey = 'eta' | 'severity' | 'confidence';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'eta', label: 'Time / ETA' },
  { key: 'severity', label: 'Severity' },
  { key: 'confidence', label: 'Confidence' },
];

const initialLabResults: LabResult[] = [
  {
    id: 'LR-78231',
    patient: 'Aria Chen',
    mrn: 'MRN-20311',
    location: 'ICU • Bed 4',
    reason: 'Sepsis workup',
    test: 'Blood Culture x2',
    status: 'processing',
    severity: 'critical',
    value: 'Pending',
    units: '',
    reference: 'N/A',
    delta: 0,
    abnormal: true,
    confidence: 0.78,
    etaMinutes: 5,
    updatedAgo: '1m ago',
  },
  {
    id: 'LR-78230',
    patient: 'Jordan Vega',
    mrn: 'MRN-19845',
    location: 'ED • Pod B',
    reason: 'Chest pain',
    test: 'Troponin I',
    status: 'flagged',
    severity: 'critical',
    value: '0.64',
    units: 'ng/mL',
    reference: '<0.04',
    delta: 132,
    abnormal: true,
    confidence: 0.91,
    etaMinutes: -2,
    updatedAgo: 'Just now',
  },
  {
    id: 'LR-78224',
    patient: 'Maya Kulkarni',
    mrn: 'MRN-12034',
    location: 'Cardiology • 12B',
    reason: 'Post-stent surveillance',
    test: 'CMP',
    status: 'resulted',
    severity: 'high',
    value: 'Na 133',
    units: 'mmol/L',
    reference: '135-145',
    delta: -2,
    abnormal: true,
    confidence: 0.96,
    etaMinutes: -18,
    updatedAgo: '6m ago',
  },
  {
    id: 'LR-78211',
    patient: 'Felix Romero',
    mrn: 'MRN-22091',
    location: 'Oncology • Infusion 5',
    reason: 'Cycle 4 labs',
    test: 'CBC + Diff',
    status: 'processing',
    severity: 'high',
    value: 'Pending',
    units: '',
    reference: 'N/A',
    delta: 0,
    abnormal: false,
    confidence: 0.84,
    etaMinutes: 12,
    updatedAgo: '3m ago',
  },
  {
    id: 'LR-78177',
    patient: 'Emily Watkins',
    mrn: 'MRN-18877',
    location: 'Med/Surg • 32A',
    reason: 'Fever post-op',
    test: 'Procalcitonin',
    status: 'queued',
    severity: 'high',
    value: 'Pending',
    units: '',
    reference: 'N/A',
    delta: 0,
    abnormal: false,
    confidence: 0.7,
    etaMinutes: 22,
    updatedAgo: '8m ago',
  },
  {
    id: 'LR-78175',
    patient: 'Luca Donati',
    mrn: 'MRN-18555',
    location: 'Clinic • Cardiac Rehab',
    reason: 'Baseline panel',
    test: 'Lipid Profile',
    status: 'resulted',
    severity: 'routine',
    value: 'LDL 82',
    units: 'mg/dL',
    reference: '<100',
    delta: -6,
    abnormal: false,
    confidence: 0.88,
    etaMinutes: -32,
    updatedAgo: '18m ago',
  },
  {
    id: 'LR-78174',
    patient: 'Omar Idris',
    mrn: 'MRN-19902',
    location: 'ED • Pod A',
    reason: 'Rule out PE',
    test: 'D-Dimer',
    status: 'resulted',
    severity: 'high',
    value: '0.61',
    units: 'mcg/mL FEU',
    reference: '<0.50',
    delta: 34,
    abnormal: true,
    confidence: 0.9,
    etaMinutes: -26,
    updatedAgo: '11m ago',
  },
  {
    id: 'LR-78168',
    patient: 'Simone Ricks',
    mrn: 'MRN-20067',
    location: 'Virtual Ward',
    reason: 'CKD monitoring',
    test: 'Basic Metabolic Panel',
    status: 'processing',
    severity: 'routine',
    value: 'Pending',
    units: '',
    reference: 'N/A',
    delta: 0,
    abnormal: false,
    confidence: 0.6,
    etaMinutes: 35,
    updatedAgo: '5m ago',
  },
];

let rollingLabCounter = 78150;

export default function CoreClinicalPage() {
  const { slug } = useParams();
  const [labResults, setLabResults] = useState<LabResult[]>(initialLabResults);
  const [severityFilter, setSeverityFilter] = useState<'all' | Severity>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [sortBy, setSortBy] = useState<SortKey>('eta');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedId, setSelectedId] = useState<string>(initialLabResults[0]?.id ?? '');

  useEffect(() => {
    const interval = setInterval(() => {
      setLabResults((prev) => simulateLabStream(prev));
    }, 5500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedId && labResults.some((row) => row.id === selectedId)) return;
    if (labResults[0]) {
      setSelectedId(labResults[0].id);
    }
  }, [labResults, selectedId]);

  const filteredResults = useMemo(() => {
    let rows = labResults;
    if (severityFilter !== 'all') {
      rows = rows.filter((row) => row.severity === severityFilter);
    }
    if (statusFilter !== 'all') {
      rows = rows.filter((row) => row.status === statusFilter);
    }
    return [...rows].sort((a, b) => compareLabs(a, b, sortBy, sortDir));
  }, [labResults, severityFilter, statusFilter, sortBy, sortDir]);

  const selectedResult =
    filteredResults.find((row) => row.id === selectedId) ??
    labResults.find((row) => row.id === selectedId) ??
    filteredResults[0];

  if (slug === 'walk-in-urgent-care') {
    return <WalkInUrgentCarePage />;
  }

  // The lab & imaging board renders fabricated demo data; only expose it in development previews.
  if (slug !== 'lab-imaging' || !SHOW_DEVELOPMENT_PREVIEWS) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center gap-4 justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-slate-400">Core Clinical • Lab & Imaging</p>
          <h1 className="text-2xl font-semibold text-ink">Live Results Triage</h1>
          <p className="text-sm text-slate-500 max-w-2xl">
            Real-time labs and imaging markers stream into a single triage board so clinicians can acknowledge, drill
            into trends, link imaging, or escalate care without leaving the feed.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm font-medium text-emerald-600">Live feed</span>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            title: 'Critical awaiting action',
            value: labResults.filter((row) => row.severity === 'critical').length,
            icon: AlertTriangle,
            tone: 'text-rose-600 bg-rose-50',
          },
          {
            title: 'Processing right now',
            value: labResults.filter((row) => row.status === 'processing').length,
            icon: Activity,
            tone: 'text-sky-600 bg-sky-50',
          },
          {
            title: 'Results cleared today',
            value: labResults.filter((row) => row.status === 'resulted').length,
            icon: CheckCircle2,
            tone: 'text-emerald-600 bg-emerald-50',
          },
        ].map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center gap-4">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${card.tone}`}>
                <Icon size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">{card.title}</p>
                <p className="text-2xl font-semibold text-ink">{card.value}</p>
              </div>
            </div>
          );
        })}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <section className="bg-white border border-slate-100 rounded-3xl shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Severity</p>
              <div className="flex flex-wrap gap-2">
                {severityOptions.map((option) => {
                  const isActive = severityFilter === option.value;
                  return (
                    <button
                      key={option.value}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                        isActive ? 'bg-ink text-white shadow-sm' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                      onClick={() => setSeverityFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</p>
              <select
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-ink/10"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as 'all' | Status)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2 ml-auto flex-wrap">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sort</p>
                {sortOptions.map((option) => {
                  const isActive = option.key === sortBy;
                  return (
                    <button
                      key={option.key}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1 transition ${
                        isActive ? 'bg-ink text-white shadow-sm' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                      onClick={() => {
                        if (isActive) {
                          setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setSortBy(option.key);
                        }
                      }}
                    >
                      {option.label}
                      {isActive && <ArrowUpDown size={16} className={sortDir === 'asc' ? 'rotate-180' : ''} />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="overflow-auto max-h-[640px]">
            <table className="min-w-full text-sm text-left">
              <thead className="text-xs uppercase tracking-wide text-slate-500 sticky top-0 bg-white border-y border-slate-100">
                <tr>
                  <th className="px-6 py-3">Patient & ID</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Test</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3">Units</th>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3 text-right">Δ vs prior</th>
                  <th className="px-4 py-3 text-center">Flag</th>
                  <th className="px-4 py-3 text-center">Confidence</th>
                  <th className="px-6 py-3 text-right">Time / ETA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredResults.map((row) => {
                  const isSelected = row.id === selectedResult?.id;
                  return (
                    <tr
                      key={row.id}
                      className={`cursor-pointer transition ${
                        isSelected ? 'bg-sky-50/70' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-ink">{row.patient}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${severityMeta[row.severity].className}`}>
                              {severityMeta[row.severity].label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">{row.mrn}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-slate-600">{row.location}</td>
                      <td className="px-4 py-4 text-slate-600">{row.reason}</td>
                      <td className="px-4 py-4 text-ink font-medium">{row.test}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusMeta[row.status].className}`}>
                          {statusMeta[row.status].label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right font-semibold text-ink">{row.value}</td>
                      <td className="px-4 py-4 text-slate-500">{row.units}</td>
                      <td className="px-4 py-4 text-slate-500">{row.reference}</td>
                      <td
                        className={`px-4 py-4 text-right font-medium ${
                          row.delta > 0 ? 'text-rose-600' : row.delta < 0 ? 'text-emerald-600' : 'text-slate-500'
                        }`}
                      >
                        {row.delta > 0 ? '+' : ''}
                        {row.delta}
                        {row.delta !== 0 && '%'}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {row.abnormal ? (
                          <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-semibold">
                            <AlertTriangle size={14} /> Abnormal
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                            <CheckCircle2 size={14} /> OK
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <p className="text-sm font-semibold text-ink">{Math.round(row.confidence * 100)}%</p>
                        <p className="text-[11px] text-slate-400">model confidence</p>
                      </td>
                      <td className="px-6 py-4 text-right text-slate-600">
                        <p className="font-medium text-ink">{formatEta(row.etaMinutes)}</p>
                        <p className="text-xs text-slate-400">{row.updatedAgo}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 space-y-5">
          {selectedResult ? (
            <>
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-slate-500">Selected result</p>
                <h2 className="text-xl font-semibold text-ink">{selectedResult.test}</h2>
                <p className="text-sm text-slate-500">
                  {selectedResult.patient} • {selectedResult.location}
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Value</span>
                  <span className="font-semibold text-ink">
                    {selectedResult.value} {selectedResult.units}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Reference</span>
                  <span className="font-semibold text-slate-700">{selectedResult.reference}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Δ vs prior</span>
                  <span
                    className={`font-semibold ${
                      selectedResult.delta > 0
                        ? 'text-rose-600'
                        : selectedResult.delta < 0
                        ? 'text-emerald-600'
                        : 'text-slate-600'
                    }`}
                  >
                    {selectedResult.delta > 0 ? '+' : ''}
                    {selectedResult.delta}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Confidence</span>
                  <span className="font-semibold text-ink">{Math.round(selectedResult.confidence * 100)}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Status</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusMeta[selectedResult.status].className}`}>
                    {statusMeta[selectedResult.status].label}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Quick actions</p>
                {[
                  { label: 'Acknowledge & remove from queue', icon: CheckCircle2 },
                  { label: 'View trend / prior encounters', icon: LineChart },
                  { label: 'Link radiology / imaging to case', icon: Scan },
                  { label: 'Order follow-up testing', icon: PlusCircle },
                  { label: 'Escalate to service line', icon: Stethoscope },
                  { label: 'Send consult update', icon: Link2 },
                ].map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.label}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border border-slate-200 text-left hover:border-ink/40 hover:bg-slate-50 transition"
                    >
                      <Icon size={18} className="text-slate-500" />
                      <span className="text-sm font-medium text-ink">{action.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Clock3 size={16} />
                  <span>ETA / turn-around target</span>
                </div>
                <p className="text-xl font-semibold text-ink">{formatEta(selectedResult.etaMinutes)}</p>
                <p className="text-xs text-slate-500">
                  Live updates arrive every few seconds. Actions sync back to the triage board instantly.
                </p>
              </div>
            </>
          ) : (
            <div className="text-center text-slate-500 text-sm">Select a row to manage quick actions.</div>
          )}
        </aside>
      </div>
    </div>
  );
}

function compareLabs(a: LabResult, b: LabResult, key: SortKey, dir: 'asc' | 'desc') {
  const direction = dir === 'asc' ? 1 : -1;
  switch (key) {
    case 'eta':
      return (a.etaMinutes - b.etaMinutes) * direction;
    case 'severity':
      return (severityRank(a.severity) - severityRank(b.severity)) * direction;
    case 'confidence':
      return (a.confidence - b.confidence) * direction;
    default:
      return 0;
  }
}

function severityRank(severity: Severity) {
  switch (severity) {
    case 'critical':
      return 3;
    case 'high':
      return 2;
    case 'routine':
      return 1;
    default:
      return 3;
  }
}

function formatEta(minutes: number) {
  if (minutes === 0) return 'Now';
  if (minutes < 0) {
    const value = Math.abs(minutes);
    return `${value}m ago`;
  }
  return `ETA ${minutes}m`;
}

function simulateLabStream(previous: LabResult[]): LabResult[] {
  let updated = previous.map((row) => {
    const nextEta = Math.max(row.etaMinutes - randomBetween(1, 4), -45);
    let nextStatus = row.status;
    if (nextEta <= 0 && nextStatus !== 'resulted') {
      nextStatus = row.abnormal ? 'flagged' : 'resulted';
    } else if (nextEta <= 12 && nextStatus === 'queued') {
      nextStatus = 'processing';
    }
    if (nextStatus === 'resulted' && row.abnormal && Math.random() > 0.7) {
      nextStatus = 'flagged';
    }
    const confidenceDrift = Math.max(Math.min(row.confidence + randomBetween(-4, 4) / 100, 0.99), 0.55);
    return {
      ...row,
      status: nextStatus,
      etaMinutes: nextEta,
      confidence: confidenceDrift,
      updatedAgo: 'Just now',
    };
  });

  if (Math.random() > 0.65) {
    updated = [
      generateIncomingLab(),
      ...updated,
    ].slice(0, 14);
  }

  return updated;
}

function generateIncomingLab(): LabResult {
  rollingLabCounter += 1;
  const patients = [
    { name: 'Priya Desai', mrn: 'MRN-23012', location: 'ED • Pod D' },
    { name: 'Kai Nguyen', mrn: 'MRN-17654', location: 'ICU • Bed 2' },
    { name: 'Lila Fernandez', mrn: 'MRN-20991', location: 'Cardiology • 9C' },
    { name: 'Marco Pelle', mrn: 'MRN-14477', location: 'Med/Surg • 21A' },
    { name: 'Monica Reyes', mrn: 'MRN-19802', location: 'Virtual Ward' },
  ];
  const tests = [
    { test: 'CRP', reason: 'Inflammation surveillance', units: 'mg/L', reference: '<5', severity: 'high' as Severity },
    { test: 'VBG + Lactate', reason: 'Shock protocol', units: 'mmol/L', reference: '0.5-2.2', severity: 'critical' as Severity },
    { test: 'Urine Culture', reason: 'UTI follow-up', units: 'cfu/mL', reference: '<10k', severity: 'routine' as Severity },
    { test: 'BNP', reason: 'CHF exacerbation', units: 'pg/mL', reference: '<100', severity: 'high' as Severity },
    { test: 'Ferritin', reason: 'Anemia workup', units: 'ng/mL', reference: '12-300', severity: 'routine' as Severity },
  ];
  const patient = patients[Math.floor(Math.random() * patients.length)];
  const test = tests[Math.floor(Math.random() * tests.length)];
  const abnormal = test.severity !== 'routine' ? Math.random() > 0.45 : Math.random() > 0.75;
  return {
    id: `LR-${rollingLabCounter}`,
    patient: patient.name,
    mrn: patient.mrn,
    location: patient.location,
    reason: test.reason,
    test: test.test,
    status: 'queued',
    severity: test.severity,
    value: 'Pending',
    units: test.units,
    reference: test.reference,
    delta: abnormal ? randomBetween(-12, 145) : randomBetween(-8, 12),
    abnormal,
    confidence: Math.random() * 0.3 + 0.6,
    etaMinutes: randomBetween(8, 32),
    updatedAgo: 'Just now',
  };
}

function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
